import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Plus, Flame, Trash2 } from "lucide-react";
import { toast } from "sonner";

function fmt(dtiso) {
  if (!dtiso) return "—";
  try { return new Date(dtiso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return dtiso; }
}

export default function ErhitzungPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ dish: "", temperature: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/heating").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.dish.trim() || form.temperature === "") { toast.error("Speise und Temperatur erforderlich"); return; }
    setBusy(true);
    try {
      await api.post("/heating", { dish: form.dish, temperature: parseFloat(form.temperature), note: form.note || null });
      toast.success("Erhitzung dokumentiert");
      setForm({ dish: "", temperature: "", note: "" });
      setOpen(false);
      load();
    } catch { toast.error("Fehler"); } finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/heating/${id}`); load(); };

  return (
    <div>
      <PageHeader title="Erhitzungsprotokoll" subtitle="Kerntemperatur beim Erhitzen · Richtwert ≥ 72 °C für 2 Min.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-heating"><Plus className="h-4 w-4 mr-1" /> Neuer Eintrag</Button>
          </DialogTrigger>
          <DialogContent data-testid="heating-dialog">
            <DialogHeader><DialogTitle>Erhitzung dokumentieren</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Speise / Gericht *</Label>
                <Input data-testid="input-heating-dish" value={form.dish} onChange={(e) => setForm({ ...form, dish: e.target.value })} placeholder="z. B. Pommes, Chicken Wings" /></div>
              <div className="space-y-1.5"><Label>Kerntemperatur (°C) *</Label>
                <Input data-testid="input-heating-temp" type="number" step="0.1" value={form.temperature} onChange={(e) => setForm({ ...form, temperature: e.target.value })} placeholder="z. B. 78" />
                {form.temperature !== "" && parseFloat(form.temperature) < 72 &&
                  <p className="text-sm text-red-600">Unter 72 °C – nicht ausreichend erhitzt!</p>}</div>
              <div className="space-y-1.5"><Label>Bemerkung</Label>
                <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="optional" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button data-testid="btn-submit-heating" onClick={submit} disabled={busy}>{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Laden …</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-400"><Flame className="h-10 w-10 mx-auto mb-2" />Noch keine Einträge</div>
      ) : (
        <div className="space-y-2">
          {items.map((i) => (
            <div key={i.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="heating-row">
              <Flame className={`h-5 w-5 ${i.ok ? "text-emerald-500" : "text-red-500"}`} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-800 text-sm">{i.dish}</div>
                <div className="text-xs text-slate-500">{fmt(i.datetime_str)}{i.note ? ` · ${i.note}` : ""}</div>
              </div>
              <span className="font-mono font-bold text-slate-900">{i.temperature}°C</span>
              {i.ok ? <StatusBadge variant="done">≥ 72 °C</StatusBadge> : <StatusBadge variant="warning">zu niedrig</StatusBadge>}
              <button onClick={() => del(i.id)} className="text-slate-300 hover:text-red-500" data-testid="btn-delete-heating"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
